import React from 'react';
import { Control, FieldErrors } from 'react-hook-form';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { Text } from '@/components/ui/therapist/Text';
import CustomCheckBox from '@/components/SharedComponents/CustomCheckBox/CustomCheckBox';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import colors from '@/config/colors';
import { View, Linking } from 'react-native';
import { SharedFormField } from '@/components/SharedComponents/FormField/SharedFormField';
import {
  confirmPasswordValidate,
  passwordValidate,
} from '@/utils/passwordUtils';
import { IS_ANDROID } from '@/utils/platform';
import { Links } from '@/config/links';
import { styles } from './styles';
import { SignUpFormData } from './SignUpFormData';

interface PatientSignUpFormProps {
  control: Control<SignUpFormData>;
  handleSubmit: () => void;
  isUseOfDataConfirmed: boolean;
  setUseOfDataConfirmation: (value: boolean) => void;
  isDataStudyConsent: boolean;
  setDataStudyConsent: (value: boolean) => void;
  isNewsletterConfirmed: boolean;
  setNewsletterConfirmation: (value: boolean) => void;
  showNewsletter: boolean;
  isSubmitted: boolean;
  filledPassword: string;
  errors: FieldErrors<SignUpFormData>;
}

export const PatientSignUpForm: React.FC<PatientSignUpFormProps> = ({
  control,
  handleSubmit,
  isUseOfDataConfirmed,
  setUseOfDataConfirmation,
  isDataStudyConsent,
  setDataStudyConsent,
  isNewsletterConfirmed,
  setNewsletterConfirmation,
  showNewsletter,
  isSubmitted,
  filledPassword,
  errors,
}) => {
  const titleText =
    'Registrieren Sie sich hier für neolexon Aphasie als Patient:in';

  const dataProtectionText = (
    <Text>
      Ich willige ein, dass meine personenbezogenen Daten, darunter auch
      Gesundheitsdaten, durch neolexon verarbeitet werden, um eine
      Sprachtherapie durchzuführen. Weitere Informationen zu meinen Rechten, der
      Speicherung und Verarbeitung der Daten finde ich in der
      Datenschutzerklärung. Ich bestätige diese gelesen zu haben. Ich kann meine
      Einwilligung jederzeit widerrufen.{' '}
      <PressableNeolexon
        onPress={() => Linking.openURL(Links.datenschutz.href)}
      >
        <Text style={styles.openLink}>Datenschutzerklärung öffnen </Text>
      </PressableNeolexon>
    </Text>
  );

  const dataProcessingAgreementText = (
    <Text>
      Ich willige ein, dass meine Nutzungsdaten anonymisiert durch neolexon
      verarbeitet werden, um die dauerhafte, technische Funktionsfähigkeit und
      Nutzerfreundlichkeit der neolexon Apps zu gewährleisten sowie diese stetig
      weiterzuentwickeln. Ich kann meine Einwilligung jederzeit widerrufen.
      Diese Einwilligung ist
      <Text style={styles.boldMessage}> optional</Text>.
    </Text>
  );

  return (
    <KeyboardAwareScrollView enableOnAndroid>
      <View style={styles.container}>
        <Text style={styles.title}>{titleText}</Text>
        <Text style={[styles.subtitle]}>
          Und testen Sie 14 Tage kostenlos. Danach entstehen keine automatischen
          Kosten.
        </Text>
        <Text style={[styles.subtitle, { color: colors.red }]}>
          Wichtig: Sie benötigen eine:n Therapeut:in, um unsere App nutzen zu
          können.
        </Text>
        <View style={styles.formWrapper}>
          <View style={styles.formField}>
            <SharedFormField
              containerStyle={styles.inputText}
              inputContainerStyle={styles.inputContainerStyle}
              errorMessageStyle={styles.errorMessage}
              control={control}
              name="firstName"
              label="Vorname"
              rules={{ required: 'Vorname wird benötigt.' }}
              autoCapitalize="words"
            />
          </View>
          <View style={styles.formField}>
            <SharedFormField
              containerStyle={styles.inputText}
              inputContainerStyle={styles.inputContainerStyle}
              errorMessageStyle={styles.errorMessage}
              control={control}
              name="lastName"
              label="Nachname"
              rules={{ required: 'Nachname wird benötigt.' }}
              autoCapitalize="words"
            />
          </View>
          <View style={styles.formField}>
            <SharedFormField
              containerStyle={styles.inputText}
              inputContainerStyle={styles.inputContainerStyle}
              errorMessageStyle={styles.errorMessage}
              control={control}
              name="email"
              label="E-Mail-Adresse"
              keyboardType="email-address"
              rules={{
                required: 'E-Mail-Adresse wird benötigt.',
                pattern: {
                  value:
                    /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/gi,
                  message: 'E-Mail-Adresse ungültig',
                },
              }}
            />
          </View>
          <View style={styles.formField}>
            <SharedFormField
              containerStyle={styles.inputText}
              inputContainerStyle={styles.inputContainerStyle}
              errorMessageStyle={styles.errorMessage}
              control={control}
              name="password"
              label="Passwort (mindestens 8 Zeichen)"
              secureTextEntry={true}
              rules={{
                required: 'Passwort wird benötigt.',
                minLength: {
                  value: 8,
                  message:
                    'Passwort muss mindestens 8 Zeichen lang sein und darf Buchstaben, Zahlen, Sonderzeichen und Leerzeichen enthalten.',
                },
                validate: (value: string) => passwordValidate(value),
              }}
            />
          </View>

          <View style={styles.formField}>
            <SharedFormField
              containerStyle={styles.inputText}
              inputContainerStyle={styles.inputContainerStyle}
              errorMessageStyle={styles.errorMessage}
              control={control}
              name="confirmPassword"
              label="Wiederholung Passwort"
              secureTextEntry={true}
              rules={{
                required: 'Passwortwiederholung wird benötigt.',
                validate: (value: string) =>
                  confirmPasswordValidate(value, filledPassword),
              }}
            />
          </View>
        </View>
        <View style={styles.checkboxWrapper}>
          <View style={styles.checkbox}>
            <CustomCheckBox
              onChecked={() => setUseOfDataConfirmation(!isUseOfDataConfirmed)}
              checked={isUseOfDataConfirmed}
              isLabelComponent={IS_ANDROID}
              label={
                <PressableNeolexon
                  onPress={() =>
                    setUseOfDataConfirmation(!isUseOfDataConfirmed)
                  }
                >
                  <Text style={styles.checkBoxText}>
                    {dataProtectionText}{' '}
                    {!isUseOfDataConfirmed ? (
                      <Text
                        style={[
                          isSubmitted
                            ? styles.mandatoryErrorCheck
                            : styles.mandatoryTextCheck,
                        ]}
                      >
                        (Pflichtfeld)
                      </Text>
                    ) : null}
                  </Text>
                </PressableNeolexon>
              }
            />
          </View>

          <View style={styles.checkbox}>
            <CustomCheckBox
              checked={isDataStudyConsent}
              onChecked={() => {
                setDataStudyConsent(!isDataStudyConsent);
              }}
              isLabelComponent={IS_ANDROID}
              label={
                <PressableNeolexon
                  onPress={() => setDataStudyConsent(!isDataStudyConsent)}
                >
                  <Text style={styles.checkBoxText}>
                    {dataProcessingAgreementText}
                  </Text>
                </PressableNeolexon>
              }
            />
          </View>

          <View style={styles.checkbox}>
            {showNewsletter ? (
              <CustomCheckBox
                checked={isNewsletterConfirmed}
                onChecked={() =>
                  setNewsletterConfirmation(!isNewsletterConfirmed)
                }
                isLabelComponent={IS_ANDROID}
                label={
                  <View>
                    <PressableNeolexon
                      onPress={() =>
                        setNewsletterConfirmation(!isNewsletterConfirmed)
                      }
                    >
                      <Text style={styles.checkBoxText}>
                        Ich möchte den Newsletter erhalten.
                      </Text>
                    </PressableNeolexon>
                  </View>
                }
              />
            ) : null}
          </View>
        </View>

        <View style={styles.subtitle2Container}>
          <Text style={styles.subtitle2}>
            Mit Ihrer Registrierung stimmen Sie den{'  '}
            <Text
              onPress={() => Linking.openURL(Links.agb.href)}
              style={[styles.openLink, styles.font20]}
            >
              {Links.agb.label}
            </Text>
            {'  '}
            zu.{'\n'}
          </Text>
        </View>

        <PressableNeolexon
          onPress={handleSubmit}
          style={styles.registrationButton}
        >
          <Text style={styles.registration}> Registrieren </Text>
        </PressableNeolexon>
      </View>
    </KeyboardAwareScrollView>
  );
};
